import * as React from 'react';
import { useContext, useEffect, useState } from 'react';
import { useDispatch } from '@/state/store';
import { API_VERSION_STORAGE_KEY, ApiVersion, VegobjekttypeCategoryState, VegobjekttypeState } from '@/state';
import { ConfigAndDataContext } from '@/state/context/ConfigAndDataContext';
import Toast from '../../objects/Toast';
import ExportDialog from '@/components/avgrensning/hva/ExportDialog';
import { changeSplash as _changeSplash } from '@/actions/splashActions';
import { handleDownload, useIsSensitiveAvailable, useUrlsParams } from '@/utils/exportUtils';
import { useSelector } from 'react-redux';
import { statePreferences } from '@/selectors/selectors';
import { getApiVersion } from '@/utils/Utils';

interface DownloadResultsBoxProps {
    votState: VegobjekttypeState;
    catState?: VegobjekttypeCategoryState;
}

export const DownloadResultsBox = (props: DownloadResultsBoxProps) => {
    const dispatch = useDispatch();
    const context = useContext(ConfigAndDataContext);

    const [exportDialogOpen, setExportDialogOpen] = useState(false);
    const [downloading, setDownloading] = useState(false);
    const [downloadUrl, setDownloadUrl] = useState('');

    const isSensitiveAvailable = useIsSensitiveAvailable(props.votState.typeId);
    const urlsParams = useUrlsParams(props.votState, props.catState);

    const preferences = useSelector(statePreferences);

    const handleQuickDownload = (e: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
        handleDownload(e, isSensitiveAvailable, downloadUrl, context, dispatch, setDownloading);
    };

    const convertFileSize = (fileSize: number) => {
        return Math.round(fileSize / 500); // returns approx. filesize in MB
    };

    const fileSizeWarning = () => {
        const { votState, catState } = props;
        if (votState?.loading || catState?.loading) return null;
        const fileSize = catState
            ? convertFileSize(catState.result.vegobjekter.length)
            : convertFileSize(votState.result.extentStatistics.antall);
        return (
            fileSize > 5 && (
                <p className="c-results-summary-export-links__file-size-warning">
                    <strong>Advarsel: </strong>
                    Stor fil (ca. {fileSize} MB). Det kan ta opptil et par minutt før filen begynner å laste
                    ned.
                </p>
            )
        );
    };

    const apiLink = () => {
        const { restapi_url, restapi_url_v3 } = context.config;
        const rest_api_url = getApiVersion() === ApiVersion.V3 ? restapi_url_v3 : restapi_url;
        const { typeId } = props.votState;
        const urlParams = `/vegobjekter/${typeId}?${urlsParams}`;
        return rest_api_url + urlParams;
    };

    return (
        <div className="c-results-summary t-hidden-mobile">
            <div className="c-results-summary__export c-results-summary-export">
                <div className="c-results-summary-export__links c-results-summary-export-links t-clearfix">
                    <a
                        className="o-icon-download c-results-summary__icon-download"
                        href={downloadUrl}
                        onClick={e => handleQuickDownload(e)}
                    />
                    <span className="c-results-summary__text">Last ned datasett:</span>
                    <button
                        title="Åpne eksportdialog"
                        id="eksport-link"
                        key="eksport-link"
                        className="c-results-summary-export-dialog"
                        onClick={() => {
                            setExportDialogOpen(true);
                        }}
                    >
                        Eksporter{' '}
                        {isSensitiveAvailable && <i className="o-icon-logout c-results-summary__icon-lock" />}
                    </button>
                    <a
                        key="api-link"
                        className="c-results-summary-export-links__api"
                        target="_blank"
                        rel="noopener noreferrer"
                        href={apiLink()}
                    >
                        API
                    </a>
                </div>
                {fileSizeWarning()}
            </div>
            <Toast text={'Nedlasting pågår'} show={downloading} />
            <ExportDialog
                isOpen={exportDialogOpen}
                setIsOpen={setExportDialogOpen}
                setDownloading={setDownloading}
                downloadUrl={downloadUrl}
                setDownloadUrl={setDownloadUrl}
                {...props}
            />
        </div>
    );
};
